import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _taggedTemplateLiteral from "@babel/runtime/helpers/esm/taggedTemplateLiteral";

var _templateObject, _templateObject2, _templateObject3, _templateObject4, _templateObject5, _templateObject6;

import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import { _js } from '@ifixit/localize';
import { Button, TextField } from '@ifixit/toolbox';
import { color, fontSize } from '@core-ds/primitives';
import Status, { StatusType } from './status';
import { post } from '../api';
/**
 * Because we have three components with different heights that swap their
 * displays on and off, we need to hard-code the container's height to be the
 * same as the height of the biggest component (in this case the confirmation)
 * in order to keep the overall size constant.
 */

var NotifyContainer = styled.div(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n   position: relative;\n   height: 63px;\n   overflow: hidden;\n"])));
var InitialBanner = styled.span(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n   display: flex;\n   position: absolute;\n   width: 100%;\n   justify-content: space-between;\n\n   transition: opacity 0.5s;\n\n   visibility: ", ";\n   opacity: ", ";\n"])), function (_ref) {
  var stage = _ref.stage;
  return stage === notifyStage.INITIAL ? 'visible' : 'hidden';
}, function (_ref2) {
  var stage = _ref2.stage;
  return stage === notifyStage.INITIAL ? 1 : 0;
});
var OutOfStock = styled.span(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n   display: flex;\n   align-items: center;\n   color: ", ";\n   font-size: ", ";\n"])), color.red[500], fontSize.md);
var EmailForm = styled.form(_templateObject4 || (_templateObject4 = _taggedTemplateLiteral(["\n   display: flex;\n   position: absolute;\n   width: 100%;\n   justify-content: space-between;\n\n   transition: all 0.5s;\n\n   ", "\n   visibility: ", ";\n   opacity: ", ";\n\n   .text-field {\n      padding-right: 10px;\n\n      > span {\n         display: none;\n      }\n   }\n"])), function (_ref3) {
  var stage = _ref3.stage;
  return stage === notifyStage.INITIAL ? 'transform: translateX(-100%);' : '';
}, function (_ref4) {
  var stage = _ref4.stage;
  return stage === notifyStage.INPUT ? 'visible' : 'hidden';
}, function (_ref5) {
  var stage = _ref5.stage;
  return stage === notifyStage.CONFIRMATION ? 0 : 1;
});
var HidableStatus = styled(Status)(_templateObject5 || (_templateObject5 = _taggedTemplateLiteral(["\n   position: absolute;\n   width: 100%;\n   box-sizing: border-box;\n\n   transition: transform 0.5s;\n\n   transform: ", ";\n   visibility: ", ";\n"])), function (_ref6) {
  var stage = _ref6.stage;
  return stage === notifyStage.CONFIRMATION ? 'translateX(0)' : 'translateX(-100%)';
}, function (_ref7) {
  var stage = _ref7.stage;
  return stage === notifyStage.CONFIRMATION ? 'visible' : 'hidden';
});
var ButtonNoShrink = styled(Button)(_templateObject6 || (_templateObject6 = _taggedTemplateLiteral(["\n   flex-shrink: 0;\n   flex-basis: auto;\n"])));
var notifyStage = {
  INITIAL: 'initial',
  INPUT: 'input',
  CONFIRMATION: 'confirmation'
};
var defaultConfirmationStatus = {
  type: StatusType.SUCCESS,
  message: _js("We'll send a message when the product is back in stock.")
};

var notifyText = _js('Notify me');

var outOfStockText = _js('Out of Stock');

var PRODUCT_CODE_LENGTH = 6;

var NotifyProduct = function NotifyProduct(_ref8) {
  var email = _ref8.email,
      sku = _ref8.sku,
      salesChannelID = _ref8.salesChannelID,
      baseUrl = _ref8.baseUrl,
      className = _ref8.className;

  var _useState = useState(notifyStage.INITIAL),
      _useState2 = _slicedToArray(_useState, 2),
      stage = _useState2[0],
      setStage = _useState2[1];

  var _useState3 = useState(defaultConfirmationStatus),
      _useState4 = _slicedToArray(_useState3, 2),
      confirmationStatus = _useState4[0],
      setConfirmationStatus = _useState4[1];

  var _useState5 = useState(email),
      _useState6 = _slicedToArray(_useState5, 2),
      formEmail = _useState6[0],
      setFormEmail = _useState6[1];

  var _useState7 = useState(false),
      _useState8 = _slicedToArray(_useState7, 2),
      showValidity = _useState8[0],
      setShowValidity = _useState8[1];

  var _useState9 = useState(false),
      _useState10 = _slicedToArray(_useState9, 2),
      buttonClicked = _useState10[0],
      setButtonClicked = _useState10[1];

  var emailForm = useRef(null);
  sku = '' + sku;

  var sendNotifyRequest = function sendNotifyRequest() {
    post(baseUrl, 'cart/product/notifyWhenInStock', {
      productcode: sku.substring(0, PRODUCT_CODE_LENGTH),
      optionid: sku.substring(PRODUCT_CODE_LENGTH),
      email: formEmail,
      sales_channelid: salesChannelID
    }).then(function (response) {
      if (response.ok) {
        return;
      } // Determine what message we want to send


      if (response.status >= 400 && response.status < 500) {
        return response.json().then(function (responseBody) {
          setConfirmationStatus({
            type: StatusType.ERROR,
            message: responseBody.message
          });
        });
      } else {
        throw new Error('Server error');
      }
    }).catch(function () {
      setConfirmationStatus({
        type: StatusType.ERROR,
        message: _js('Request failed')
      });
    }).then(function () {
      setStage(notifyStage.CONFIRMATION);
    });
  };

  return /*#__PURE__*/React.createElement(NotifyContainer, {
    className: className
  }, /*#__PURE__*/React.createElement(InitialBanner, {
    stage: stage
  }, /*#__PURE__*/React.createElement(Button, {
    design: "outline",
    onClick: function onClick() {
      return setStage(notifyStage.INPUT);
    }
  }, notifyText), /*#__PURE__*/React.createElement(OutOfStock, null, outOfStockText)), /*#__PURE__*/React.createElement(EmailForm, {
    stage: stage,
    ref: emailForm
  }, /*#__PURE__*/React.createElement(TextField, {
    showValidity: showValidity,
    required: true,
    type: "email",
    className: "text-field",
    value: formEmail,
    placeholder: _js('Enter your email'),
    onChange: function onChange(_ref9) {
      var value = _ref9.value;
      return setFormEmail(value);
    }
  }), /*#__PURE__*/React.createElement(ButtonNoShrink, {
    design: "primary",
    disabled: buttonClicked,
    onClick: function onClick(event) {
      event.preventDefault();

      if (emailForm.current && emailForm.current.checkValidity()) {
        setButtonClicked(true);
        sendNotifyRequest();
      } else {
        setShowValidity(true);
      }
    }
  }, notifyText)), /*#__PURE__*/React.createElement(HidableStatus, {
    stage: stage,
    type: confirmationStatus.type
  }, confirmationStatus.message));
};

export default NotifyProduct;